import { useState } from 'react';
import type { Command } from '@integrate/contracts';
import { api, type StagedRefresh } from '../api.js';
import { Modal } from '../ui/Modal.js';

/*
  The mid-engagement roster refresh, which is the first thing a live deal needs and the last thing to
  get a way in.

  `applyDataUpdate` has been complete on the server since P3c — ported with seven of the original's
  defects fixed rather than reproduced, and the one command with an explicit multi-scenario test — and
  reachable only by `curl`. Rosters change weekly on a real carve-out, so this is the difference between
  a platform somebody can run an engagement on and one they can be shown.

  TWO STEPS, AND THE FIRST ONE IS THE POINT. Staging writes nothing: it parses the file under the
  project's current mapping, diffs it against the roster, and returns what would happen. Only then is
  there anything to confirm. The shipped app's version of this asked once and told you afterwards — a
  refresh could mark a hundred people leavers, delete their RemainCo mitigation plans, silently re-apply
  the perimeter rule and leave the undo stack at length 1, and the only thing on screen was a count. So
  the preview is not a courtesy here; it is the feature.

  WHAT IT DOES NOT DO. It does not decide anything. Every refusal below — a missing mapped column, a
  changed mapping, a roster that moved since the preview — is the SERVER's, quoted back. This screen
  predicts them where it can, because a button whose only outcome is a refusal is worse than a button
  that says why it is disabled, but it never enforces one of its own.
*/
export function RefreshModal({
  projectId,
  isOwner,
  onDispatch,
  onClose,
}: {
  projectId: string;
  /**
   * OWNER-ONLY, not merely writable — `applyDataUpdate` is in `OWNER_ONLY_COMMANDS`.
   *
   * Checked here because an editor can legitimately STAGE a run (staging needs only write access) and
   * would then be refused at the apply, having already uploaded a file and read a preview. Offering the
   * upload to somebody who cannot finish is the "button whose only outcome is a refusal" shape this
   * codebase keeps closing; saying so up front is the cheaper version of the same honesty.
   */
  isOwner: boolean;
  /**
   * The command path every other write goes through — a refresh is not a special case.
   *
   * Returns whether it landed. `dispatch` surfaces a refusal through the error banner and resolves
   * NORMALLY, so a `.catch` here would never run: without the boolean this screen would say "the roster
   * was refreshed" on top of a 409 saying it had not been.
   */
  onDispatch(command: Command): Promise<boolean>;
  onClose(): void;
}): JSX.Element {
  const [staged, setStaged] = useState<StagedRefresh | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);

  const stage = (file: File): void => {
    setBusy(true);
    setError('');
    setStaged(null);
    setApplied(false);
    void api
      .stageDataUpdate(projectId, file)
      .then(setStaged)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'That file could not be read.'),
      )
      .finally(() => setBusy(false));
  };

  const apply = (): void => {
    if (!staged || busy) return;
    setBusy(true);
    setError('');
    void onDispatch({
      type: 'applyDataUpdate',
      runId: staged.runId,
      /*
        WHAT THE REVIEWER WAS LOOKING AT, sent back so the server can refuse if it is no longer true.
        A colleague adding a build-up between the preview and this click changes the population the diff
        was computed against, and the counts above would then describe a project that no longer exists.
        Cheap, and it catches the one case the shipped app could not: it had no preview to be stale.
      */
      expectRosterCount: staged.rosterCount,
    })
      .then((landed) => {
        /*
          ONLY ON A YES. A refusal has already put its own sentence in the error banner — the server's
          words, which are the ones worth reading: a missing column, a mapping that changed, a roster
          that moved since the preview. Saying "refreshed" beside it would be the screen contradicting
          the only source that knows.
        */
        if (landed) setApplied(true);
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'The refresh could not be applied.'),
      )
      .finally(() => setBusy(false));
  };

  const p = staged?.preview;
  // Predicted, not enforced — the server refuses this too, and its message is the one that matters.
  const blocked = (staged?.missingColumns.length ?? 0) > 0;

  return (
    <Modal
      testId="refresh-modal"
      title="Refresh the roster"
      onClose={onClose}
    >
      <p className="muted small">
        Upload a new extract to bring joiners, leavers and changed attributes into the project.
        Nothing is written until you have read what it would do.{' '}
        <strong>Leavers are marked, never deleted</strong> — they still price severance and still
        anchor the audit trail.
      </p>

      {!isOwner && (
        <p className="muted small" data-testid="refresh-not-owner">
          Only a project owner can refresh the roster. It replaces the population for everybody, in
          every scenario, so it is not a change an editor makes on their own.
        </p>
      )}

      <input
        type="file"
        accept=".xlsx,.xls"
        data-testid="refresh-file"
        disabled={busy || !isOwner}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) stage(file);
        }}
      />

      {busy && !staged && (
        <p className="muted" data-testid="refresh-reading">
          Reading the file…
        </p>
      )}

      {error && (
        <p className="error" data-testid="refresh-error" role="alert">
          {error}
        </p>
      )}

      {staged && p && !applied && (
        <div data-testid="refresh-preview">
          <p className="muted small">
            <strong>{staged.filename}</strong> · {staged.rowCount}{' '}
            {staged.rowCount === 1 ? 'row' : 'rows'}
          </p>

          {blocked && (
            /*
              The refusal worth predicting, because it is the one that silently destroys data if it
              gets through: a mapped column the file does not have would be applied as blank for
              everybody. The shipped app WARNED here and carried on.
            */
            <p className="error" data-testid="refresh-missing-columns">
              This file is missing mapped columns: {staged.missingColumns.join(', ')}. Applying it
              would blank those fields for everybody, so it will be refused. Re-export with those
              columns, or change the mapping first.
            </p>
          )}

          <ul className="hlist" data-testid="refresh-counts">
            <li data-testid="refresh-joiners">{p.joiners} joining</li>
            <li data-testid="refresh-leavers">{p.leavers} leaving</li>
            <li data-testid="refresh-movers">{p.movers} changed</li>
            <li className="muted" data-testid="refresh-unchanged">
              {p.unchanged} unchanged
            </li>
            {p.stillDeparted > 0 && (
              <li className="muted" data-testid="refresh-still-departed">
                {p.stillDeparted} already marked as left
              </li>
            )}
          </ul>

          <Sample
            testId="refresh-sample-leavers"
            title="Leaving"
            total={p.leavers}
            names={p.sampleLeavers.map((x) => x.name)}
          />
          <Sample
            testId="refresh-sample-joiners"
            title="Joining"
            total={p.joiners}
            names={p.sampleJoiners.map((x) => x.name)}
          />
          <Sample
            testId="refresh-sample-movers"
            title="Changed"
            total={p.movers}
            names={p.sampleMovers.map((x) => `${x.name} (${x.fields.join(', ')})`)}
          />

          <p className="muted small">
            {/*
              The honest caveat about the numbers above, and the reason `expectRosterCount` exists.
            */}
            These are read from the project as it stands now. If a colleague changes the roster
            before you apply, the refresh is refused rather than applied against different numbers.
          </p>

          <div className="sp-row">
            <button
              className="primary"
              data-testid="refresh-apply"
              disabled={busy || blocked || !isOwner}
              onClick={apply}
            >
              {busy ? 'Applying…' : 'Apply the refresh'}
            </button>
          </div>
        </div>
      )}

      {applied && (
        <p data-testid="refresh-applied">
          The roster was refreshed. It is one entry in the change log and can be undone from there,
          like any other change.
        </p>
      )}
    </Modal>
  );
}

/**
 * A capped list of names under a count.
 *
 * NAMES RATHER THAN A NUMBER, because "43 leaving" is not something anybody can check and "Ada Lovelace,
 * Alan Turing, …" is. The server caps the sample at 20; the remainder is stated rather than left to be
 * inferred from a list that stops.
 */
function Sample({
  testId,
  title,
  total,
  names,
}: {
  testId: string;
  title: string;
  total: number;
  names: string[];
}): JSX.Element | null {
  if (!names.length) return null;
  const rest = total - names.length;
  return (
    <div data-testid={testId}>
      <p className="muted small">
        {title}: {names.join(', ')}
        {rest > 0 ? `, and ${rest} more` : ''}
      </p>
    </div>
  );
}
